import { InteractionRequiredAuthError } from "@azure/msal-browser";

/**
 * Obtain an ID token from MSAL accounts.
 * @param {object} msalInstance - MSAL PublicClientApplication instance (from useMsal()).
 * @param {array} accounts - Array of MSAL account objects (from useMsal()).
 * @returns {Promise<string>} - Resolves to ID token string, or throws if not available.
 */
export async function getIdTokenFromMsal(msalInstance, accounts) {
  if (!accounts || accounts.length === 0) {
    throw new Error("No MSAL accounts available");
  }
  
  const activeAccount = accounts[0];
  
  // First try to get ID token from the account object
  if (activeAccount.idToken) {
    return activeAccount.idToken;
  }
  
  // If no cached ID token, acquire a new one
  try {
    const request = {
      scopes: ["openid", "profile"],
      account: activeAccount
    };
    
    const response = await msalInstance.acquireTokenSilent(request);
    return response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ["openid", "profile"],
        account: activeAccount
      });
      return response.idToken;
    }
    throw error;
  }
}

/**
 * Get a fresh ID token with force refresh option
 * @param {object} msalInstance - MSAL PublicClientApplication instance
 * @param {array} accounts - Array of MSAL account objects
 * @param {boolean} forceRefresh - Force acquire new token
 * @returns {Promise<string>} - ID token string
 */
export async function getFreshIdToken(msalInstance, accounts, forceRefresh = false) {
  if (!accounts || accounts.length === 0) {
    throw new Error("No MSAL accounts available");
  }
  
  const activeAccount = accounts[0];

  try {
    const request = {
      scopes: ["openid", "profile"],
      account: activeAccount,
      forceRefresh: forceRefresh
    };
    
    const response = await msalInstance.acquireTokenSilent(request);
    return response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ["openid", "profile"],
        account: activeAccount
      });
      return response.idToken;
    }
    throw error;
  }
}